import { useState } from 'react';
import { useAuthStore, useUIStore } from '@/stores';
import { Button, Input, Loader, DotSpinner } from '@/components/ui';
import { User, Lock, ExternalLink, Mail, ArrowRight } from 'lucide-react';
import MetaBalls from '@/components/MetaBalls';
import { getEmailProvider } from '@/utils';
import bgImage from '../../../public/PDT COVER 2.png';

export function AuthModal() {
  const { isAuthModalOpen, closeAuthModal } = useUIStore();
  const { login, register, isLoading } = useAuthStore();
  
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  if (!isAuthModalOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (isLoginMode) {
        await login(email, password);
        setSuccess('登录成功！');
        setTimeout(() => {
          closeAuthModal();
        }, 1000);
      } else {
        await register(email, password);
        setSuccess('注册成功！请检查邮箱验证。');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || '操作失败，请重试');
    }
  };

  const toggleMode = () => {
    setIsLoginMode(!isLoginMode);
    setError('');
    setSuccess('');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden" style={{ backgroundColor: '#2148C0' }}>
      {/* Background decoration - 恢复原来的 SVG 背景 */}
      <div className="absolute inset-0 pointer-events-none">
        <svg className="absolute w-full h-full" viewBox="0 0 1642 1083" preserveAspectRatio="xMidYMid slice" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="362" cy="721" r="362" fill="#264ECA"/>
          <circle cx="362" cy="721" r="286" fill="#244BC5"/>
          <circle cx="362" cy="721" r="219" fill="#274FC7"/>
          <path d="M1034.5 140C898.9 152.8 806.667 52 777.5 0L1641.5 3.5V721.5H1545C1246.2 683.5 1296.5 532 1359 461C1395.5 411.167 1461.5 291.1 1433.5 209.5C1398.5 107.5 1204 124 1034.5 140Z" fill="#264ECA"/>
        </svg>
      </div>

      {/* 响应式布局：竖屏和横屏都显示左右分栏 */}
      <div className="w-[95%] h-[90%] sm:w-[90%] sm:h-auto sm:max-w-4xl modal-enter relative z-10 flex bg-white rounded-lg overflow-hidden shadow-2xl">
        {/* Left Panel - Illustration */}
        <div 
          className="hidden sm:block relative w-[55%] overflow-hidden"
          style={{ 
            background: `linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #60a5fa 100%) url(${bgImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundBlendMode: 'overlay'
          }}
        >
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-blue-900/20 to-transparent pointer-events-none" />
          
          {/* Center Logo */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative w-[280px] h-[220px]">
              <MetaBalls
                color="#264ECA"
                cursorBallColor="#ffffff"
                speed={0.15}
                ballCount={10}
                animationSize={20}
                cursorBallSize={2}
                clumpFactor={1}
                enableMouseInteraction={true}
                enableTransparency={true}
                hoverSmoothness={0.08}
                className="w-full h-full"
              />
              <img src="/logo-cropped.png" alt="Logo" className="absolute inset-0 w-full h-full object-contain z-10" />
            </div>
          </div>
        </div>

        {/* Right Panel - Form */}
        <div className="w-full sm:w-[45%] flex items-center justify-center p-6 sm:p-8 bg-white overflow-y-auto max-h-full">
          <div className="w-full max-w-sm">
            {/* Mobile Logo */}
            <div className="sm:hidden flex justify-center mb-6">
              <img src="/logo-cropped.png" alt="Logo" className="w-[180px] h-[150px] object-contain" />
            </div>

            {/* Header */}
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 text-center sm:text-left">
              {isLoginMode ? <>Hello PDT<sub className="text-base sm:text-xl">er</sub>!</> : 'Create Account'}
            </h2>
            <p className="text-gray-500 mb-6 text-center sm:text-left">
              {isLoginMode ? 'Welcome Back' : 'Sign up to get started'}
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                    <Mail className="w-5 h-5" />
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full h-11 pl-10 pr-4 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm"
                    placeholder="Enter your email"
                    required
                  />
                </div>
              </div>

              {/* Password Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                    <Lock className="w-5 h-5" />
                  </div>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full h-11 pl-10 pr-4 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm"
                    placeholder="Enter your password"
                    required
                    minLength={6}
                  />
                </div>
              </div>

              {/* Error/Success Messages */}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                  {error}
                </div>
              )}
              {success && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-600 text-sm">
                  {success}
                  {!isLoginMode && getEmailProvider(email) && (
                    <div className="mt-2">
                      <a
                        href={getEmailProvider(email)!.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                          setTimeout(() => {
                            setIsLoginMode(true);
                            setSuccess('');
                          }, 100);
                        }}
                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium underline transition-colors"
                      >
                        前往 {getEmailProvider(email)!.name}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  )}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
              >
                {isLoading ? (
                  isLoginMode ? <DotSpinner /> : <Loader size="sm" className="mx-auto" />
                ) : (
                  <>
                    {isLoginMode ? 'Login' : 'Sign Up'}
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            {/* Toggle Mode */}
            <div className="mt-6 text-center">
              <p className="text-gray-600 text-sm">
                {isLoginMode ? "Don't have an account?" : 'Already have an account?'}
                <button
                  type="button"
                  onClick={toggleMode}
                  className="ml-2 text-blue-600 hover:text-blue-700 font-semibold"
                >
                  {isLoginMode ? 'Sign Up' : 'Login'}
                </button>
              </p>
            </div>

            {/* Divider */}
            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-4 bg-white text-gray-500">Or continue with</span>
                </div>
              </div>

              {/* Social Login Buttons */}
              <div className="mt-4 grid grid-cols-2 gap-3">
                <button className="flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200">
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="currentColor"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  <span className="text-xs font-medium text-gray-700">Google</span>
                </button>
                <button className="flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                  </svg>
                  <span className="text-xs font-medium text-gray-700">GitHub</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
